import React, { useMemo } from "react";
import t from "../../../../../../i18n";
import ReportingChart from "../../../ReportingChart";
import FailureAggregationSelector from "../FailureAggregationSelector";
import PerformanceIndicatorSelector from "../../../PerformanceIndicatorSelector";
import ReportingPeriodSelector from "../../../ReportingPeriodSelector";
import { useSeries } from "./useSeries";
import useVisibleSeries from "@ui/Highcharts/hooks/useVisibleSeries";
import { useOptions } from "./useOptions";
import { css } from "@linaria/core";
import { useTranslations } from "../../../../../../api/useTranslations";
import type { DiagnosisReporting } from "../../../../../../api/useReporting";

const styledCrossHair = css`
  .custom-highcharts-crosshair {
    transform: scale(10);
  }
`;

function FleetFailureImpactEvolution({ reporting }: { reporting: DiagnosisReporting }) {
  const translations = useTranslations();
  const chart = React.useRef<Highcharts.Chart>(null);
  const series = useSeries(reporting);

  const chartDefinition = useMemo(
    () => ({
      id: "FleetFailureImpactEvolution",
      title: t("reporting.failure_impacts.title"),
      description: {
        title: t("reporting.chart.info.title"),
        description: t("reporting.failure_impacts.info.description", translations),
        options: [
          {
            value: "INTERNAL_GAPS_OPTION",
            label: t("reporting.internal_gaps", translations),
            description: t("reporting.internal_gaps.description"),
          },
        ],
      },
    }),
    [translations],
  );

  const [visibleSeries] = useVisibleSeries(chartDefinition.id, series);
  const options = useOptions(chart, {
    reporting,
    reportingPeriod: reporting.range,
    series: visibleSeries,
  });

  return options && series ? (
    <ReportingChart
      chartRef={chart}
      reporting={reporting}
      id={chartDefinition.id}
      title={chartDefinition.title}
      description={chartDefinition.description}
      leftOptions={
        <>
          <FailureAggregationSelector />
          <PerformanceIndicatorSelector reporting={reporting} />
        </>
      }
      rightOptions={
        <>
          <ReportingPeriodSelector reporting={reporting} />
        </>
      }
      selectablePoints
      series={visibleSeries}
      options={options}
      className={styledCrossHair}
    />
  ) : null;
}

export default React.memo(FleetFailureImpactEvolution);
